import React from "react";
import { useMutation } from "@apollo/client";
import type { EditProps, ListProps, Record } from "react-admin";
import { useHistory } from "react-router";
import {
	List,
	Datagrid,
	TextField,
	ImageField,
	SimpleForm,
	EditButton,
	useNotify,
	useRefresh,
	Button,
	FunctionField,
	useGetOne,
} from "react-admin";
import ImageUrlField from "~/components/image-url-field.tsx";
import LoadMorePagination from "~/components/load-more-pagination.tsx";
import SignedUploadInput from "~/components/signed-upload-input.tsx";
import approveTestimonialMutation from "./approve-testimonial-mutation.graphql";
import type {
	ApproveTestimonialMutation,
	ApproveTestimonialMutationVariables,
} from "./approve-testimonial-mutation.generated.ts";
import type {
	RejectTestimonialMutation,
	RejectTestimonialMutationVariables,
} from "./reject-testimonial-mutation.generated.ts";
import rejectTestimonialMutation from "./reject-testimonial-mutation.graphql";
import type {
	UpdateTestimonialMutation,
	UpdateTestimonialMutationVariables,
} from "./update-testimonial-mutation.generated.ts";
import updateTestimonialMutation from "./update-testimonial-mutation.graphql";
import TimezoneDateField from "~/components/date-field.tsx";

function ApproveButton({ record: { id } }: any) {
	const notify = useNotify();
	const refresh = useRefresh();
	const [approveTestimonial, { loading }] = useMutation<
		ApproveTestimonialMutation,
		ApproveTestimonialMutationVariables
	>(approveTestimonialMutation, {
		onError(e) {
			notify(e.toString(), { type: "error" });
		},
	});

	const onClick = async () => {
		await approveTestimonial({
			variables: {
				id,
			},
		});
		refresh();
		notify("Testimonial approved", { type: "info" });
	};

	return (
		<Button
			type="button"
			onClick={onClick}
			label="Approve"
			disabled={loading}
		/>
	);
}

function RejectButton({ record: { id } }: any) {
	const notify = useNotify();
	const refresh = useRefresh();
	const [rejectTestimonial, { loading }] = useMutation<
		RejectTestimonialMutation,
		RejectTestimonialMutationVariables
	>(rejectTestimonialMutation, {
		onError(e) {
			notify(e.toString(), { type: "error" });
		},
	});

	const onClick = async () => {
		await rejectTestimonial({
			variables: {
				id,
			},
		});
		refresh();
		notify("Testimonial rejected", { type: "info" });
	};

	return (
		<Button type="button" onClick={onClick} label="Reject" disabled={loading} />
	);
}

function PixAPicTestimonialList(props: ListProps) {
	return (
		<List
			{...props}
			title="Pix-a-pic testimonials"
			pagination={<LoadMorePagination idField="id" />}
			exporter={false}
			sort={{ field: "createdAt", order: "DESC" }}
			bulkActionButtons={false}
		>
			<Datagrid>
				<TimezoneDateField source="createdAt" showTime sortable={false} />
				<TextField source="text" sortable={false} />
				<ImageField
					label="User's uploaded image"
					source="imageUrl"
					sortable={false}
				/>
				<ImageField
					label="Public image (displayed in app)"
					source="publicImageUrl"
					sortable={false}
				/>
				<FunctionField
					source="approved"
					label="Approved"
					sortable={false}
					render={(record?: Record) => {
						if (!record) {
							return <div />;
						}
						switch (record.approved) {
							case 1:
								return "Yes";
							case 0:
								return "No";
							default:
								return null;
						}
					}}
				/>
				<EditButton />
				<FunctionField
					label="Review"
					sortable={false}
					render={(record?: Record) => {
						if (!record) {
							return <div />;
						}

						return (
							<>
								{record.approved !== 1 && <ApproveButton record={record} />}
								{record.approved !== 0 && <RejectButton record={record} />}
							</>
						);
					}}
				/>
			</Datagrid>
		</List>
	);
}

function PixAPicTestimonialEdit({ id }: EditProps) {
	const { data /* error, */ } = useGetOne("pixAPicTestimonials", id!);

	const notify = useNotify();
	const refresh = useRefresh();
	const [update, { loading: saving }] = useMutation<
		UpdateTestimonialMutation,
		UpdateTestimonialMutationVariables
	>(updateTestimonialMutation);
	const history = useHistory();

	const onSubmit = async ({ publicImage }: any) => {
		await update({
			variables: {
				id: id!,
				publicImage: publicImage ?? undefined,
			},
		});
		refresh();
		notify("Testimonial public image added", { type: "info" });
		history.push("/pixAPicTestimonials");
	};

	if (!data) {
		return <em>Loading...</em>;
	}

	return (
		<SimpleForm
			initialValues={{
				publicImage: data.publicImage,
			}}
			saving={saving}
			save={onSubmit}
		>
			<SignedUploadInput source="publicImage" directory="pix-a-pic-testimonial">
				<ImageUrlField />
			</SignedUploadInput>
		</SimpleForm>
	);
}

export { PixAPicTestimonialEdit, PixAPicTestimonialList };
